import React, { useRef, useState, useEffect } from "react";
import { theme } from "../../Themes/Themes";
import "./MobileLandingPage.css";
import DiscordButtonSVG from "../../Media/DiscordLogo";
// import playAndStepsGif from "../../Media/gifs/play-and-steps.gif";

import customUsecaseGif from "../../Media/gifs/custom-usecase.gif";
import contextAndIoGif from "../../Media/gifs/context-and-io.gif";

import linkingGif from "../../Media/gifs/CodeLinkingCompressed.gif";
import docsGif from "../../Media/gifs/DocsCompressed.gif";


import scannerGif from "../../Media/gifs/CodeCanvasScannerCompressed.gif";
import safeGif from "../../Media/gifs/save.gif";

import chatGif from "../../Media/gifs/trimmed-chat-with-completion.gif";

// Preload all GIFs
const preloadImage = (src: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const allGifs = [
  customUsecaseGif,
  contextAndIoGif,
  linkingGif,
  docsGif,
  scannerGif,
  safeGif,
  chatGif
];

const NOTION_URL = "https://pie-crepe-38f.notion.site/Documentation-cf20cbdf134247cb93bf6366d9055076";
const SUPADEMO_URL = "https://app.supademo.com/embed/cmbz9nz9rhkgnsn1r1qp9tr09?embed_v=2"; // Updated Supademo embed URL
declare module 'react' {
  interface IframeHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitallowfullscreen?: string;
    mozallowfullscreen?: string;
    allowfullscreen?: boolean;
  }
}

interface Feature {
  key: string;
  heading: string;
  subheading: string;
  visual: string;
  gifs: string[];
  gifCaptions: string[];
}

const features: Feature[] = [
  {
    key: "ai-diagrams",
    heading: "Diagrams & Context: Just Ask!",
    subheading: "Use AI to auto-generate diagrams and gain contextual understanding in seconds.",
    visual: "🤖🗺️",
    gifs: [customUsecaseGif, contextAndIoGif],
    gifCaptions: [
      "Just ask about a use case and get started understanding - no prior language experience needed!",
      "Get mocked details and simulation flow through the code responsible for the use case."
    ]
  },
  {
    key: "docs-diagrams",
    heading: "Link Docs & Diagrams to Code",
    subheading: "Keep your documents and diagrams compact, and directly linked to code wherever you have your repository.",
    visual: "📄➔💻",
    gifs: [linkingGif, docsGif],
    gifCaptions: [
      "Make your diagrams plans first and link code as you work.",
      "Add team specific context to any code snippet."
    ]
  },
  {
    key: "github-integration",
    heading: "Save Context with GitHub Integration",
    subheading: "Use our GitHub integration to save precious historical context and let engineers understand your changes years into the future.",
    visual: "🐙🕰️",
    gifs: [safeGif, scannerGif],
    gifCaptions: [
      "Push the diagram file directly to your branch or save it locally.",
      "Try enabling our Scanner which will remind you to update any diagram elements which have been affected in a PR!"
    ]
  },
  {
    key: "ai-chat",
    heading: "AI + Team Knowledge",
    subheading: "Combine your specialized team knowledge with the speed of AI to make the most informed decisions that actually fits your business constraints.",
    visual: "💬⚡",
    gifs: [chatGif],
    gifCaptions: [
      "Select paths in the code base to give exactly the context needed by LLMs to help guide your decisions."
    ]
  }
];



const MobileLandingPage: React.FC = () => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // Preload all GIFs when component mounts
  useEffect(() => {
    const loadAllImages = async () => {
      try {
        await Promise.all(allGifs.map(gif => preloadImage(gif)));
        setImagesLoaded(true);
      } catch (error) {
        console.error('Failed to preload images:', error);
        // Still set as loaded to show images even if preload fails
        setImagesLoaded(true);
      }
    };
    loadAllImages();
  }, []);

  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDocumentation = () => {
    window.location.assign(NOTION_URL);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        Email: email
      };

      const res = await fetch(
        "https://api.apispreadsheets.com/data/0fZMDWK0g77XZOUl/",
        {
          method: "POST",
          body: JSON.stringify({ data: data }),
        }
      );

      if (res.status === 201) {
        localStorage.setItem("emailEntered", "true");
        setSubmitted(true);
      } else {
        throw new Error(
          `Request failed with status: ${res.status} and msg: ${res.statusText}`
        );
      }
    } catch (e) {
      setError("Failed to submit. Please try again later.");
      console.error("Submission failed:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLearnHow = () => {
    if (featuresRef.current) {
      featuresRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="mobile-landing-root">
      {/* Sticky Floating Banner */}
      <div className="mobile-floating-banner">
        <div className="mobile-floating-banner-title">CodeCanvas</div>
        <button className="mobile-floating-banner-btn" onClick={scrollToTop}>
          Get Started
        </button>
      </div>
      <div className="mobile-landing-scroll-container">
        <div ref={topRef} className="mobile-landing-hero">
          <div className="mobile-landing-content">
            <div className="mobile-landing-hero-points">
              Generate code-level diagrams and onboard to any codebase with <br /> <span style={{ 
                color: '#ffffff', 
                fontWeight: '700', 
                backgroundColor: '#ff8b8b',
                padding: '4px 8px',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                border: '1px solid #ffaea6'
              }}>one click</span>.
            </div>
  
            
            {/* Email Submission Form */}
            <form onSubmit={handleSubmit} className="mobile-landing-form">
              <div className="mobile-email-caption" style={{ maxWidth: '400px' }}>
                We'll remind you to check out CodeCanvas on your desktop!
              </div>
              {submitted ? (
                <div className="mobile-landing-success" style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '400px', width: '100%' }}>
                  Thank you! We'll be in touch soon.
                </div>
              ) : (
                <div className="mobile-email-container" style={{ maxWidth: '400px' }}>
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="mobile-email-input"
                  />
                  <button 
                    type="submit" 
                    className="mobile-go-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '...' : 'Go'}
                  </button>
                </div>
              )}
              {error && <div className="mobile-landing-error" style={{ maxWidth: '400px', width: '100%' }}>{error}</div>}
            </form>

            <button className="mobile-landing-btn secondary" onClick={handleDocumentation}>
              Documentation Page
            </button>

            {/* Demo and Learn How Section */}
            <div className="mobile-landing-demo-learnhow-wrapper">
              <div className="mobile-landing-demo">
                <iframe
                  src={SUPADEMO_URL}
                  title="Supademo"
                  allow="clipboard-write"
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  allowfullscreen
                  allowFullScreen
                  loading="eager"
                  style={{
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    maxHeight: '100%',
                    display: 'block',
                    background: 'transparent',
                    aspectRatio: '16/9'
                  }}
                />
              </div>
              <div className="mobile-landing-learnhow" onClick={handleLearnHow}>
                Learn How<br /><span className="arrow">↓</span>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div ref={featuresRef} className="mobile-features-scroll-container">
          {features.map((feature, idx) => (
            <div key={feature.key} className="mobile-feature-scroll-section">
              <div className="mobile-feature-content-wrapper">
                <h2 className="mobile-feature-title">{feature.heading}</h2>
                <p className="mobile-feature-subtitle">{feature.subheading}</p>
                <div className="mobile-feature-gif-placeholder">
                  <div className="gif-placeholder-content">
                    {feature.gifs.map((gif, gifIdx) => (
                      <div key={`${feature.key}-gif-${gifIdx}`} className="gif-container">
                        {!imagesLoaded ? (
                          <div className="gif-loading-placeholder" />
                        ) : (
                          <img
                            src={gif}
                            alt={`${feature.heading} example ${gifIdx + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '30px',
                            }}
                            loading="eager"
                            decoding="async"
                          />
                        )}
                        <p className="gif-caption">{feature.gifCaptions[gifIdx]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Final CTA Section */}
        <div className="mobile-features-cta-section" style={{ position: 'relative', paddingBottom: '80px' }}>
          <h2 className="mobile-features-cta-title">Build as fast as you can think.</h2>
          <p className="mobile-features-cta-subtitle">
            Transform how you talk about and interact with your codebase to have more fun building.
          </p>
          <button className="mobile-features-getstarted" onClick={scrollToTop}>
            Leave Us Your Email
          </button>
          
          {/* Contact Section */}
          <div className="mobile-contact-section" style={{ 
            marginTop: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px'
          }}>
            <p style={{ 
              fontSize: '16px',
              color: '#ffffff',
              textAlign: 'center' 
            }}>
              Have questions, ideas, or thoughts? Join our Discord community!
            </p>
            <a 
              href="https://discord.gg/codecanvas" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                color: '#ffffff',
                backgroundColor: '#ffffff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                padding: '8px'
              }}
            >
              <DiscordButtonSVG />
            </a>
          </div>

          {/* Terms Section */}
          <div style={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#ffffff',
            padding: '20px',
            textAlign: 'center',
            fontSize: '14px',
            color: theme.custom.darkGrey,
          }}>
            By continuing, you agree to our{" "}
            <a
              href="https://pie-crepe-38f.notion.site/CodeCanvas-Terms-and-Conditions-ef6b3e7a3ee54da3b363c48112062bce?pvs=4"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: theme.custom.pink, textDecoration: 'none' }}
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href="https://pie-crepe-38f.notion.site/CodeCanvas-Privacy-Policy-5466713f74724c9ab0da9a1d669ab379?pvs=4"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: theme.custom.pink, textDecoration: 'none' }}
            >
              privacy policy
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileLandingPage; 